// src/catalog/launch/catalogLaunchConfigurations.js

function getRawLaunchConfigurations(entry) {
  const configs =
    entry?.metadata?.ux?.launchConfigurations;

  return Array.isArray(configs)
    ? configs.filter(Boolean)
    : [];
}

export function isContextualLaunchConfiguration(configuration) {
  return configuration?.id === "moderate-session";
}

export function isSelfDirectedLaunchConfiguration(configuration) {
  return !isContextualLaunchConfiguration(configuration);
}

function isConfigurationRoleAllowed(configuration, userRole) {
  const allowedRoles =
    configuration?.audience?.allowedRoles;

  if (!Array.isArray(allowedRoles) || allowedRoles.length === 0) {
    return true;
  }

  return Boolean(userRole && allowedRoles.includes(userRole));
}

function getRoleFilteredLaunchConfigurations(entry, { userRole } = {}) {
  return getRawLaunchConfigurations(entry).filter((configuration) =>
    isConfigurationRoleAllowed(configuration, userRole)
  );
}

export function getAvailableLaunchConfigurations(entry, { userRole } = {}) {
  return getRoleFilteredLaunchConfigurations(entry, { userRole });
}

export function getSelfDirectedLaunchConfigurations(
  entry,
  options = {}
) {
  const {
    respectAudience = false,
    userRole
  } = options;

  const launchConfigurations = respectAudience
    ? getRoleFilteredLaunchConfigurations(entry, { userRole })
    : getRawLaunchConfigurations(entry);

  return launchConfigurations.filter(isSelfDirectedLaunchConfiguration);
}

export function resolveLaunchConfigurationSelectionFromList(
  configurations = [],
  requestedLaunchConfigurationId,
  options = {}
) {
  const availableConfigurations =
    Array.isArray(configurations)
      ? configurations.filter(Boolean)
      : [];
  const preferredLaunchConfigurationId =
    options.preferredLaunchConfigurationId || null;

  const requestedConfiguration =
    availableConfigurations.find((configuration) =>
      configuration?.id === requestedLaunchConfigurationId
    ) || null;

  const preferredConfiguration =
    !requestedConfiguration && preferredLaunchConfigurationId
      ? availableConfigurations.find((configuration) =>
          configuration?.id === preferredLaunchConfigurationId
        ) || null
      : null;

  const selectedConfiguration =
    requestedConfiguration ||
    preferredConfiguration ||
    availableConfigurations.find((configuration) =>
      configuration?.isDefault === true
    ) ||
    availableConfigurations[0] ||
    null;

  return {
    availableConfigurations,
    selectedConfiguration,
    requestedConfiguration,
    preferredConfiguration,
    hasRequestedSelection:
      Boolean(requestedLaunchConfigurationId)
  };
}

export function getDefaultLaunchConfiguration(entry, options = {}) {
  return resolveLaunchConfigurationSelectionFromList(
    getAvailableLaunchConfigurations(entry, options),
    null,
    options
  ).selectedConfiguration;
}

export function getPreferredLaunchConfigurationIdForEntry(entry) {
  const preferredLaunchConfigurationId =
    entry?.viewerContext?.preferredLaunchConfigurationId;

  return typeof preferredLaunchConfigurationId === "string" &&
    preferredLaunchConfigurationId.trim()
    ? preferredLaunchConfigurationId
    : null;
}

export function resolveLaunchConfigurationSelection(
  entry,
  requestedLaunchConfigurationId,
  options = {}
) {
  return resolveLaunchConfigurationSelectionFromList(
    getAvailableLaunchConfigurations(entry, options),
    requestedLaunchConfigurationId,
    options
  );
}

export function shouldRenderLaunchConfigurationSelector(
  entry,
  options = {}
) {
  return getAvailableLaunchConfigurations(entry, options).length > 1;
}
